<template>
  <div class="auto-infracao-page">
    <div class="page-header">
      <div>
        <h2>Auto de Infração</h2>
        <p>Registro e acompanhamento de autos de infração lavrados pelo serviço de inspeção.</p>
      </div>

      <div class="header-actions">
        <button class="btn btn-secondary" type="button">Exportar</button>
        <button class="btn btn-primary" type="button" @click="adicionar">Salvar Registro</button>
      </div>
    </div>

    <div class="summary-cards">
      <div class="summary-card">
        <span class="summary-label">Total de registros</span>
        <strong>{{ lista.length }}</strong>
      </div>

      <div class="summary-card">
        <span class="summary-label">Em análise</span>
        <strong>{{ totalEmAnalise }}</strong>
      </div>

      <div class="summary-card">
        <span class="summary-label">Multas</span>
        <strong>{{ totalMultas }}</strong>
      </div>

      <div class="summary-card">
        <span class="summary-label">Infrações graves</span>
        <strong>{{ totalGraves }}</strong>
      </div>
    </div>

    <div class="content-grid">
      <!-- FORMULÁRIO -->
      <section class="panel form-panel">
        <div class="panel-header">
          <h3>Novo Registro</h3>
          <span class="panel-subtitle">Preencha os dados do auto de infração</span>
        </div>

        <div class="panel-body">
          <div class="form-grid">
            <div class="field">
              <label>Nº Processo</label>
              <input v-model="form.numero" placeholder="Ex.: AI-002" />
            </div>

            <div class="field">
              <label>Data</label>
              <input type="date" v-model="form.data" />
            </div>

            <div class="field field-span-2">
              <label>Estabelecimento</label>
              <input v-model="form.estabelecimento" placeholder="Nome do estabelecimento" />
            </div>

            <div class="field">
              <label>Fiscal Responsável</label>
              <input v-model="form.fiscal" placeholder="Nome do fiscal" />
            </div>

            <div class="field">
              <label>Classificação</label>
              <select v-model="form.classificacao">
                <option>Leve</option>
                <option>Média</option>
                <option>Grave</option>
              </select>
            </div>

            <div class="field">
              <label>Penalidade</label>
              <select v-model="form.penalidade">
                <option>Advertência</option>
                <option>Multa</option>
                <option>Interdição</option>
                <option>Apreensão</option>
              </select>
            </div>

            <div class="field field-span-2">
              <label>Base Legal</label>
              <input v-model="form.legislacao" placeholder="Informe a base legal aplicável" />
            </div>

            <div class="field field-span-2">
              <label>Descrição da Infração</label>
              <textarea
                v-model="form.descricao"
                rows="4"
                placeholder="Descreva a infração constatada"
              />
            </div>

            <div class="field field-span-2">
              <label>Observações</label>
              <textarea
                v-model="form.observacoes"
                rows="3"
                placeholder="Informações complementares"
              />
            </div>
          </div>

          <div class="form-actions">
            <button class="btn btn-light" type="button" @click="limpar">Limpar</button>
            <button class="btn btn-primary" type="button" @click="adicionar">Adicionar Registro</button>
          </div>
        </div>
      </section>

      <!-- LISTA -->
      <section class="panel list-panel">
        <div class="panel-header">
          <h3>Autos Registrados</h3>
          <span class="panel-subtitle">Consulta rápida dos registros lançados</span>
        </div>

        <div class="panel-body">
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>Nº</th>
                  <th>Data</th>
                  <th>Estabelecimento</th>
                  <th>Classificação</th>
                  <th>Penalidade</th>
                  <th>Situação</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in lista" :key="item.id">
                  <td class="fw-600">{{ item.numero }}</td>
                  <td>{{ formatarData(item.data) }}</td>
                  <td>{{ item.estabelecimento }}</td>
                  <td>
                    <span class="badge" :class="badgeClassificacao(item.classificacao)">
                      {{ item.classificacao }}
                    </span>
                  </td>
                  <td>
                    <span class="badge badge-neutral">
                      {{ item.penalidade }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" :class="badgeSituacao(item.situacao)">
                      {{ item.situacao }}
                    </span>
                  </td>
                </tr>

                <tr v-if="!lista.length">
                  <td colspan="6" class="empty-state">
                    Nenhum auto de infração registrado.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'

const lista = ref([
  {
    id: 1,
    numero: 'AI-001',
    data: '2026-03-01',
    estabelecimento: 'Frigorífico São João',
    classificacao: 'Grave',
    penalidade: 'Multa',
    situacao: 'Em análise'
  },
  {
    id: 2,
    numero: 'AI-002',
    data: '2026-03-04',
    estabelecimento: 'Laticínio Vale Verde',
    classificacao: 'Média',
    penalidade: 'Advertência',
    situacao: 'Notificado'
  }
])

const formPadrao = () => ({
  numero: '',
  data: '',
  estabelecimento: '',
  fiscal: '',
  classificacao: 'Leve',
  penalidade: 'Advertência',
  legislacao: '',
  descricao: '',
  observacoes: ''
})

const form = reactive(formPadrao())

const totalEmAnalise = computed(() =>
  lista.value.filter(item => item.situacao === 'Em análise').length
)

const totalMultas = computed(() =>
  lista.value.filter(item => item.penalidade === 'Multa').length
)

const totalGraves = computed(() =>
  lista.value.filter(item => item.classificacao === 'Grave').length
)

function adicionar() {
  lista.value.unshift({
    ...form,
    id: Date.now(),
    situacao: 'Em análise'
  })

  limpar()
}

function limpar() {
  Object.assign(form, formPadrao())
}

function formatarData(data) {
  if (!data) return ''
  const [ano, mes, dia] = data.split('-')
  return `${dia}/${mes}/${ano}`
}

function badgeClassificacao(classificacao) {
  if (classificacao === 'Grave') return 'badge-danger'
  if (classificacao === 'Média') return 'badge-warning'
  return 'badge-success'
}

function badgeSituacao(situacao) {
  if (situacao === 'Em análise') return 'badge-info'
  if (situacao === 'Notificado') return 'badge-warning'
  if (situacao === 'Concluído') return 'badge-success'
  return 'badge-neutral'
}
</script>

<style scoped>
.auto-infracao-page {
  padding: 24px;
  background: #f4f7fb;
  min-height: 100vh;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 6px;
  font-size: 28px;
  color: #1f2937;
}

.page-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.summary-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 18px 20px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  border: 1px solid #e5e7eb;
}

.summary-card strong {
  display: block;
  margin-top: 8px;
  font-size: 28px;
  color: #111827;
}

.summary-label {
  font-size: 13px;
  color: #6b7280;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 20px;
  align-items: start;
}

.panel {
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.panel-header {
  padding: 18px 22px;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(to right, #f9fbff, #f4f7fb);
}

.panel-header h3 {
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

.panel-subtitle {
  display: inline-block;
  margin-top: 4px;
  font-size: 13px;
  color: #6b7280;
}

.panel-body {
  padding: 22px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
}

.field-span-2 {
  grid-column: span 2;
}

.field label {
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.field input,
.field select,
.field textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 11px 12px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  transition: 0.2s ease;
  box-sizing: border-box;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.field textarea {
  resize: vertical;
  min-height: 90px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.btn {
  border: none;
  border-radius: 12px;
  padding: 11px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s ease;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #e5e7eb;
  color: #1f2937;
}

.btn-secondary:hover {
  background: #d1d5db;
}

.btn-light {
  background: #f3f4f6;
  color: #374151;
}

.btn-light:hover {
  background: #e5e7eb;
}

.table-wrapper {
  overflow-x: auto;
}

.custom-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.custom-table thead th {
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  color: #4b5563;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.custom-table tbody td {
  padding: 14px;
  border-bottom: 1px solid #eef2f7;
  font-size: 14px;
  color: #1f2937;
  vertical-align: middle;
}

.custom-table tbody tr:hover {
  background: #f8fbff;
}

.fw-600 {
  font-weight: 600;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.badge-danger {
  background: #fee2e2;
  color: #b91c1c;
}

.badge-warning {
  background: #fef3c7;
  color: #b45309;
}

.badge-success {
  background: #dcfce7;
  color: #15803d;
}

.badge-info {
  background: #dbeafe;
  color: #1d4ed8;
}

.badge-neutral {
  background: #e5e7eb;
  color: #374151;
}

.empty-state {
  text-align: center;
  color: #6b7280;
  padding: 24px !important;
}

@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .summary-cards {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }
}

@media (max-width: 700px) {
  .auto-infracao-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .summary-cards {
    grid-template-columns: 1fr;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .field-span-2 {
    grid-column: span 1;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>